// Карточка «Установить приложение» в настройках (браузерный режим, приложение ещё не установлено).
// Сама решает, показываться ли: в Telegram, в установленной PWA и после установки — ничего не рендерит.
import React, { useState } from 'react';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { isAndroid, isInAppBrowser, isIos, isIosSafari, isStandalone } from '../utils/device';
import { isTelegramMiniApp } from '../utils/platform';
import { promptInstall, useInstallState } from '../services/installPrompt';
import { MUTED_TEXT_COLOR } from '../styles/tokens';

const ACCENT = '#ff7043';
const TEXT_COLOR = '#d4d4d8';

const IOS_PUSH_NOTE = 'Push-уведомления на iPhone работают только в установленном приложении (iOS 16.4 и новее).';

function Text({ children, muted, sx }) {
  return (
    <Typography sx={{ fontSize: '14px', color: muted ? MUTED_TEXT_COLOR : TEXT_COLOR, lineHeight: 1.5, ...sx }}>
      {children}
    </Typography>
  );
}

function IosSafariSteps() {
  return (
    <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box component="li"><Text>Нажмите «Поделиться» (квадрат со стрелкой вверх) внизу экрана.</Text></Box>
      <Box component="li"><Text>Выберите «На экран Домой» и нажмите «Добавить».</Text></Box>
      <Box component="li"><Text>Откройте Pro Resto с экрана «Домой».</Text></Box>
    </Box>
  );
}

function BrowserMenuHint() {
  if (isAndroid()) {
    return <Text>Откройте меню браузера (⋮) → «Установить приложение» или «Добавить на главный экран».</Text>;
  }
  return (
    <Text>
      В Chrome, Edge или Яндекс Браузере: значок установки в адресной строке справа или меню (⋮) → «Установить
      приложение». Firefox и Safari на ПК установку приложений не поддерживают.
    </Text>
  );
}

function InstallContent() {
  const { canPrompt, dismissed } = useInstallState();
  const [busy, setBusy] = useState(false);

  if (isInAppBrowser()) {
    return (
      <>
        <Text>Откройте сайт в обычном браузере (Chrome, Safari, Яндекс Браузер), чтобы установить приложение.</Text>
        <Text muted sx={{ mt: 0.5, fontSize: '13px' }}>Подсказка: меню «⋯» → «Открыть в браузере».</Text>
      </>
    );
  }

  if (isIos()) {
    return (
      <>
        {isIosSafari() ? (
          <IosSafariSteps />
        ) : (
          <Text>
            Нажмите «Поделиться» в меню браузера → «На экран Домой». Если такого пункта нет — откройте сайт в Safari и
            добавьте оттуда.
          </Text>
        )}
        <Text muted sx={{ mt: 1, fontSize: '13px' }}>{IOS_PUSH_NOTE}</Text>
      </>
    );
  }

  if (canPrompt) {
    const handleInstall = async () => {
      setBusy(true);
      try {
        await promptInstall();
      } finally {
        setBusy(false);
      }
    };
    return (
      <>
        <Text>Приложение откроется в отдельном окне, без адресной строки, — как обычное.</Text>
        <Button
          onClick={handleInstall}
          disabled={busy}
          sx={{
            mt: 1.5,
            height: 40,
            px: 2.5,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            color: '#1a0f0a',
            backgroundColor: ACCENT,
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#ff8a65', boxShadow: 'none' },
          }}
        >
          {busy ? <CircularProgress size={18} sx={{ color: '#1a0f0a' }} /> : 'Установить'}
        </Button>
      </>
    );
  }

  // Браузер установку не предложил (или от неё отказались — повторно покажет не сразу)
  return (
    <>
      {dismissed && <Text muted sx={{ mb: 0.5, fontSize: '13px' }}>Установить можно и позже из меню браузера:</Text>}
      <BrowserMenuHint />
    </>
  );
}

export default function InstallAppCard({ sx }) {
  const { installed } = useInstallState();
  if (isTelegramMiniApp() || isStandalone() || installed) return null;

  return (
    <Grid item xs={12}>
      <Box id="install-app" sx={sx}>
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: { md: 40 }, mb: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>Установить приложение</Typography>
        </Box>
        <InstallContent />
      </Box>
    </Grid>
  );
}
